import { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface PercentCellProps {
  value: number; // 0-100
  onChange: (value: number) => void;
  isEditing: boolean;
  onStartEdit: () => void;
  onEndEdit: () => void;
  showProgressBar?: boolean;
  progressColor?: string;
  className?: string;
}

export function PercentCell({
  value,
  onChange,
  isEditing,
  onStartEdit,
  onEndEdit,
  showProgressBar = true,
  progressColor = '#3b82f6',
  className,
}: PercentCellProps) {
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditValue((value ?? 0).toString());
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [isEditing, value]);

  const parsePercent = useCallback((str: string): number => {
    const cleaned = str.replace(/[^0-9.-]/g, '');
    let num = parseFloat(cleaned);
    if (isNaN(num)) num = 0;
    // Clamp to 0-100
    num = Math.max(0, Math.min(100, num));
    return Math.round(num);
  }, []);

  const handleSave = useCallback(() => {
    const parsed = parsePercent(editValue);
    if (parsed !== value) {
      onChange(parsed);
    }
    onEndEdit();
  }, [editValue, value, parsePercent, onChange, onEndEdit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditValue((value ?? 0).toString());
        onEndEdit();
      } else if (e.key === 'Tab') {
        handleSave();
      }
    },
    [handleSave, onEndEdit, value]
  );

  const safeValue = value ?? 0;

  // Determine color based on progress
  const getProgressColor = () => {
    if (progressColor !== '#3b82f6') return progressColor;
    if (safeValue >= 100) return '#22c55e'; // Green for complete
    if (safeValue >= 50) return '#3b82f6'; // Blue for in progress
    return '#f59e0b'; // Amber for low progress
  };

  if (isEditing) {
    return (
      <div className={cn('w-full h-full relative', className)}>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full h-full px-2 py-1 text-sm text-right bg-white border-2 border-blue-500 outline-none',
            'rounded-none focus:ring-0 pr-6'
          )}
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
          %
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full h-full px-2 py-1 flex items-center gap-2 cursor-text',
        className
      )}
      onDoubleClick={onStartEdit}
    >
      {showProgressBar ? (
        <>
          <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${safeValue}%`,
                backgroundColor: getProgressColor(),
              }}
            />
          </div>
          <span className="text-xs text-gray-600 min-w-[32px] text-right">
            {safeValue}%
          </span>
        </>
      ) : (
        <span className="text-sm text-right w-full">{value}%</span>
      )}
    </div>
  );
}

export default PercentCell;
